import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layers, Settings2 } from 'lucide-react';
import type { Block, SeoMeta, TranslatableString } from '@geoic/shared';
import { Input } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { cn } from '@/lib/cn.js';
import { useAuth } from '@/features/auth/auth-context.js';
import { BlockConstructor } from '@/features/blocks/BlockConstructor.js';
import { ContentFormLayout } from '../ContentFormLayout.js';
import { DraftBanner } from '../DraftBanner.js';
import { SeoPanel, emptySeo } from '../SeoPanel.js';
import { useContentForm } from '../useContentForm.js';
import { pagesApi, type PageRecord } from './pages-api.js';

type PageForm = {
  slug: string;
  title: TranslatableString;
  blocks: Block[];
  seo: SeoMeta;
};

type Tab = 'blocks' | 'settings';

/**
 * Sahifa tahrirlash — blok konstruktori shu yerda (D4).
 *
 * Ikki yorliq: «Bloklar» va «Sozlamalar». Bitta ekranda birlashtirilsa,
 * konstruktor uchun joy qolmasdi — u ekranning butun balandligini
 * egallashi kerak.
 */
export function PageEditPage() {
  const { id = 'new' } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('blocks');

  const form = useContentForm<PageForm>({
    collection: 'pages',
    contentApi: pagesApi,
    id,
    basePath: '/pages',
    empty: () => ({
      slug: '',
      title: { uz: '', ru: '', en: '' },
      blocks: [],
      seo: emptySeo(),
    }),
    fromRecord: (record) => {
      const page = record as PageRecord;
      return {
        slug: page.slug ?? '',
        title: page.title ?? { uz: '', ru: '', en: '' },
        blocks: page.blocks ?? [],
        seo: page.seo ?? emptySeo(),
      };
    },
    toInput: (value) => ({
      slug: value.slug,
      title: value.title,
      blocks: value.blocks,
      seo: value.seo,
    }),
  });

  const page = form.record as PageRecord | null;
  const isSystem = page?.isSystem === true;

  if (form.loading) {
    return <p className="p-6 text-sm text-n-600">Yuklanmoqda…</p>;
  }

  if (form.notFound) {
    return <p className="p-6 text-sm text-error-600">Sahifa topilmadi yoki o‘chirilgan.</p>;
  }

  return (
    <ContentFormLayout
      title={form.form.title.uz || (form.isNew ? 'Yangi sahifa' : '(nomsiz sahifa)')}
      backTo="/pages"
      backLabel="Sahifalar"
      isNew={form.isNew}
      isPublished={form.isPublished}
      saving={form.saving}
      publishing={form.publishing}
      canPublish={user?.role !== 'editor'}
      autosave={form.autosave}
      lock={form.lock}
      unsaved={form.unsaved}
      onSave={form.save}
      onTogglePublish={form.togglePublish}
      onPreview={form.preview}
      actions={
        <div className="flex border border-n-200 p-0.5">
          <TabButton active={tab === 'blocks'} onClick={() => setTab('blocks')}>
            <Layers className="size-4" aria-hidden />
            Bloklar
            <span className="text-xs text-n-400">{form.form.blocks.length}</span>
          </TabButton>
          <TabButton active={tab === 'settings'} onClick={() => setTab('settings')}>
            <Settings2 className="size-4" aria-hidden />
            Sozlamalar
          </TabButton>
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 sm:p-6">
        <DraftBanner
          savedAt={form.pendingDraft ? (page?.draftAt ?? null) : null}
          onRestore={form.restoreDraft}
          onDiscard={form.discardDraft}
        />

        {tab === 'blocks' ? (
          <BlockConstructor
            blocks={form.form.blocks}
            role={user?.role ?? 'editor'}
            onChange={(blocks) => form.setForm((current) => ({ ...current, blocks }))}
          />
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title="Asosiy" />
              <CardBody className="space-y-4">
                <TranslatableInput
                  label="Sarlavha"
                  required
                  value={form.form.title}
                  onChange={(title) => form.setForm((current) => ({ ...current, title }))}
                  error={form.fieldErrors['title.uz']}
                />

                <Input
                  label="Manzil (slug)"
                  value={form.form.slug}
                  disabled={isSystem}
                  placeholder="markaz-haqida"
                  onChange={(event) =>
                    form.setForm((current) => ({ ...current, slug: event.target.value }))
                  }
                  error={form.fieldErrors.slug}
                  hint={
                    isSystem
                      ? 'Tizim sahifasining manzili o‘zgartirilmaydi'
                      : 'Bo‘sh qoldirilsa sarlavhadan avtomatik yasaladi. Bosh sahifa uchun bo‘sh'
                  }
                />

                {isSystem && (
                  <p className=" bg-n-50 px-3 py-2 text-sm text-n-600">
                    Bu tizim sahifasi — uni o‘chirib bo‘lmaydi, chunki saytda unga doimiy havolalar
                    bor.
                  </p>
                )}
              </CardBody>
            </Card>

            <SeoPanel
              value={form.form.seo}
              onChange={(seo) => form.setForm((current) => ({ ...current, seo }))}
              fallbackTitle={form.form.title.uz || undefined}
            />
          </div>
        )}
      </div>
    </ContentFormLayout>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors',
        active ? 'bg-brand-600 text-white' : 'text-n-600 hover:bg-n-100',
      )}
    >
      {children}
    </button>
  );
}
